// Simpler Präferenz-Store auf Basis von localStorage.
//
// Warum nicht einfach useState + localStorage direkt in jeder Komponente?
// → Weil mehrere Komponenten die gleiche Präferenz lesen/schreiben sollen
//   (z.B. ProfilePage schreibt den Screen-Wake-Toggle, App.tsx liest ihn für
//   den Wake-Lock-Hook). Ohne einen gemeinsamen Sync-Mechanismus würde der
//   Root-Hook den neuen Wert erst nach einem Reload mitkriegen.
//
// Sync-Strategie:
//   1) localStorage als Source of Truth (persistiert, Cross-Tab-fähig)
//   2) 'storage'-Event für Cross-Tab-Sync (Browser fires ihn nur in ANDEREN Tabs)
//   3) Custom Event 'adt:pref-change' für Same-Tab-Sync (der kritische Pfad bei uns)

import { useCallback, useEffect, useState } from 'react';

const EVENT_NAME = 'adt:pref-change';

interface PrefEventDetail {
  key: string;
  value: string | null;
}

function readPref(key: string, defaultValue: string): string {
  try {
    const v = localStorage.getItem(key);
    return v === null ? defaultValue : v;
  } catch {
    return defaultValue;
  }
}

/**
 * String-Präferenz aus localStorage. Synct automatisch zwischen allen
 * Komponenten im selben Tab via Custom-Event und über Tabs via storage-Event.
 */
export function usePreference(
  key: string,
  defaultValue: string,
): [string, (val: string) => void] {
  const [value, setValue] = useState<string>(() => readPref(key, defaultValue));

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<PrefEventDetail>).detail;
      if (detail?.key === key) {
        setValue(detail.value ?? defaultValue);
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) {
        setValue(e.newValue ?? defaultValue);
      }
    };
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [key, defaultValue]);

  const update = useCallback(
    (val: string) => {
      try {
        localStorage.setItem(key, val);
      } catch {
        /* private mode / quota — nicht fatal, UI-State bleibt in diesem Tab konsistent */
      }
      window.dispatchEvent(
        new CustomEvent<PrefEventDetail>(EVENT_NAME, { detail: { key, value: val } }),
      );
    },
    [key],
  );

  return [value, update];
}

/** Bool-Wrapper um usePreference — speichert '1'/'0' in localStorage. */
export function useBoolPreference(
  key: string,
  defaultValue: boolean,
): [boolean, (v: boolean) => void] {
  const [raw, setRaw] = usePreference(key, defaultValue ? '1' : '0');
  return [raw === '1', (v: boolean) => setRaw(v ? '1' : '0')];
}

/**
 * Number-Wrapper um usePreference — parsed Float, invalid/NaN → default.
 * Für UI-Slider/-Selects gedacht (Zoomstufen, Profil-Höhe, etc.).
 */
export function useNumberPreference(
  key: string,
  defaultValue: number,
): [number, (v: number) => void] {
  const [raw, setRaw] = usePreference(key, String(defaultValue));
  const parsed = Number.parseFloat(raw);
  const safe = Number.isFinite(parsed) ? parsed : defaultValue;
  return [safe, (v: number) => setRaw(String(v))];
}
